/**
 * PageJsonLd — reusable JSON-LD schema injector for public pages.
 *
 * Supports (all optional):
 *   - breadcrumbs: [{ name, url }]
 *   - faq: [{ q, a }]
 *   - howTo: { name, totalTime, steps: [string] }
 *   - article: { headline, description, datePublished, dateModified, author, image }
 *   - webApp: { name, url, description }
 *   - custom: raw schema.org object (or array) merged in
 *
 * Renders <script type="application/ld+json"> tags into <head> via portal.
 * Idempotent: cleans up on unmount / re-mount so pages don't leak schemas.
 */
import { useEffect, useMemo } from "react";

const SITE_URL = "https://centralrentals.ca";
const SCHEMA_ID_PREFIX = "crc-page-jsonld";

function buildSchemas({ breadcrumbs, faq, howTo, article, webApp, custom }) {
  const schemas = [];

  if (Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: b.url,
      })),
    });
  }

  if (Array.isArray(faq) && faq.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  if (howTo && Array.isArray(howTo.steps) && howTo.steps.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: howTo.name,
      ...(howTo.totalTime ? { totalTime: howTo.totalTime } : {}),
      step: howTo.steps.map((text, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: `Step ${i + 1}`,
        text,
      })),
    });
  }

  if (article) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.headline,
      description: article.description,
      datePublished: article.datePublished,
      dateModified: article.dateModified || article.datePublished,
      author: {
        "@type": article.author?.type || "Organization",
        name: article.author?.name || "Central Rentals Canada",
      },
      publisher: {
        "@type": "Organization",
        name: "Central Rentals Canada",
        logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
      },
      ...(article.image ? { image: article.image } : {}),
    });
  }

  if (webApp) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: webApp.name,
      url: webApp.url,
      description: webApp.description,
      applicationCategory: "FinanceApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "CAD" },
    });
  }

  if (custom) {
    if (Array.isArray(custom)) schemas.push(...custom);
    else schemas.push(custom);
  }

  return schemas;
}

export default function PageJsonLd(props) {
  const schemas = useMemo(() => buildSchemas(props), [props]);

  useEffect(() => {
    const ids = [];
    schemas.forEach((schema, i) => {
      const id = `${SCHEMA_ID_PREFIX}-${i}`;
      ids.push(id);
      document.getElementById(id)?.remove();
      const script = document.createElement("script");
      script.id = id;
      script.type = "application/ld+json";
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    });
    return () => {
      ids.forEach((id) => document.getElementById(id)?.remove());
    };
  }, [schemas]);

  return null;
}
